import Color from '@/Enums/Color.enum';
import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import type { GetOptionDisabled } from '@/types/Controls/TabSelect.types';
import {
  CircularProgress,
  MenuItem,
  MenuList,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
  typographyClasses,
} from '@mui/material';
import debounce from 'debounce';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import SearchIcon from '../Icons/SearchIcon';
import GroupOptionsRenderer from './GroupedOptionsRenderer';
import OptionsRenderer from './OptionsRenderer';
import SelectAllOption from './SelectAllOption';
import ActionButton from './styled/ActionButton';
import StyledInput from './styled/StyledInput';

enum ContainsToggleValue {
  Contains = 'contains',
  DoesNotContain = 'does_not_contain',
}

interface IProps<T> {
  options: T[];
  value: T[];
  label: string;
  loading?: boolean;
  formGroupSx?: SxProps<Theme>;
  grouped?: boolean;
  confirmLabel?: string;
  onChange: (value: T[]) => void;
  onClose: (...args: any[]) => void;
  onSubmit: () => void;
  getOptionDisabled?: GetOptionDisabled<T>;
  withContainsControl?: boolean;
  /**Show `Select All` option. `true` by default. */
  withAllOption?: boolean;
}

const toggleOptions = [
  {
    key: ContainsToggleValue.Contains,
    displayLabel: 'Contains',
  },
  {
    key: ContainsToggleValue.DoesNotContain,
    displayLabel: 'Does Not Contain',
  },
];

function TabSelect<T extends IControlOption<any, any> = IControlOption | IGroupedControlOption>({
  onChange,
  options = [],
  value = [],
  label,
  onClose,
  onSubmit,
  getOptionDisabled,
  loading,
  formGroupSx,
  grouped = false,
  confirmLabel = 'Add Filter',
  withContainsControl = false,
  withAllOption = true,
}: IProps<T>) {
  //#region state
  const [search, setSearch] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [contains, setContains] = useState<ContainsToggleValue>(ContainsToggleValue.Contains);
  //#endregion

  //#region memo
  const displayedOptions = useMemo(() => {
    if (!searchTerm.length) {
      return options ?? [];
    }
    return (options ?? []).filter(option => {
      const found = option.label.toLowerCase().includes(searchTerm);
      if (contains === ContainsToggleValue.DoesNotContain) {
        return !found;
      }
      return found;
    });
  }, [searchTerm, options, contains]);

  const selectableOptions = useMemo(
    () =>
      getOptionDisabled
        ? displayedOptions.reduce<T[]>(
            (selected, option) => (getOptionDisabled(option, selected, false) ? selected : [...selected, option]),
            [],
          )
        : displayedOptions,
    [displayedOptions, getOptionDisabled],
  );

  const allSelected = useMemo(
    () => (value?.length ?? 0) === selectableOptions.length,
    [value?.length, selectableOptions.length],
  );

  const partialSelection = useMemo(() => searchTerm.length > 0, [searchTerm.length]);
  //#endregion

  //#region callback
  const handleClearOptions = useCallback(() => onChange([]), [onChange]);

  const updateSearchTerm = useCallback(
    debounce((newSearch: string) => {
      setSearchTerm(newSearch.trim().toLowerCase());
    }, 200),
    [],
  );

  const onSearchValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSearch = e.target.value || '';
      setSearch(newSearch);
      updateSearchTerm(newSearch);
    },
    [updateSearchTerm],
  );

  const onContainsChange = useCallback((e: SelectChangeEvent) => {
    setContains(e.target.value as ContainsToggleValue);
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChange(selectableOptions);
      } else {
        handleClearOptions();
      }
    },
    [selectableOptions, handleClearOptions, onChange],
  );

  const handleToggleSelectAll = useCallback(() => {
    handleSelectAll?.(!allSelected);
  }, [allSelected, handleSelectAll]);

  const handleChange = useCallback(
    (eventValue: IControlOption | IGroupedControlOption, checked: boolean) => {
      if (checked) {
        return onChange([...value, eventValue] as T[]);
      }

      return onChange(value?.filter(selected => selected.value !== eventValue.value) ?? []);
    },
    [onChange, value],
  );
  //#endregion

  if (loading) {
    return (
      <div className='h-full flex items-center justify-center'>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className='grid p-[10px] pb-0'>
        <div className='flex gap-2 mb-2'>
          <StyledInput
            fullWidth
            startAdornment={<SearchIcon className={`text-[${Color.Grey}]`} />}
            autoFocus
            placeholder='Type to search'
            value={search}
            onChange={onSearchValueChange}
          />
          {!withContainsControl ? null : (
            <Select sx={{ minWidth: '190px' }} value={contains} onChange={onContainsChange}>
              {toggleOptions.map(({ key, displayLabel }) => (
                <MenuItem key={key} value={key}>
                  {displayLabel}
                </MenuItem>
              ))}
            </Select>
          )}
        </div>
        {displayedOptions.length && withAllOption ? (
          <>
            <SelectAllOption
              total={displayedOptions.length}
              indeterminate={!allSelected && !!value?.length}
              checked={allSelected}
              onChange={handleToggleSelectAll}
              label={label}
              partialSelection={partialSelection}
            />
            <hr />
          </>
        ) : null}
        <MenuList
          sx={{
            maxHeight: '270px',
            overflow: 'auto',
            flexWrap: 'nowrap',
            maxWidth: '100%',
            [`& .${typographyClasses.root}`]: {
              fontSize: 14,
              fontWeight: 500,
            },
            ...formGroupSx,
          }}
        >
          {grouped ? (
            <GroupOptionsRenderer
              value={value as unknown as IGroupedControlOption[]}
              options={displayedOptions as unknown as IGroupedControlOption[]}
              onChange={handleChange}
              getOptionDisabled={getOptionDisabled as unknown as GetOptionDisabled<IGroupedControlOption>}
            />
          ) : (
            <OptionsRenderer
              value={value}
              options={displayedOptions}
              onChange={handleChange}
              getOptionDisabled={getOptionDisabled as unknown as GetOptionDisabled<IControlOption>}
            />
          )}
          {displayedOptions.length ? null : <div className='my-4 text-center'>No Results</div>}
        </MenuList>
      </div>
      <hr />
      <div className='flex gap-[12px] p-[18px]'>
        <ActionButton variant='outlined' onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton variant='contained' onClick={onSubmit}>
          {confirmLabel}
        </ActionButton>
      </div>
    </>
  );
}

export default TabSelect;
